/**
 * SmartSaveScreen — explorateur intelligent des fichiers produits.
 *
 * Flux complet :
 *   1. L'utilisateur clique "Choisir un dossier" → dialogue Electron natif
 *   2. UNIQUE scanne le contenu du dossier (fichiers à sa racine)
 *   3. L'utilisateur saisit ses critères de classement libres
 *   4. Clic "Ranger par IA" → OpenRouter propose une cible pour chaque fichier
 *   5. L'utilisateur voit les décisions et peut les appliquer
 *   6. "Appliquer" déplace réellement les fichiers sur le disque
 *
 * En mode web (sans Electron), affiche un message expliquant que la
 * sélection de dossier nécessite l'app desktop.
 */
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import {
  FolderOpen,
  FileText,
  Share2,
  Archive,
  Eye,
  Wand2,
  Check,
  Sparkles,
  FolderSearch,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react';
import { UniqueButton } from '@components/UniqueButton';
import { UniqueCard } from '@components/UniqueCard';
import { UniqueBadge } from '@components/UniqueBadge';
import { AIService, type SmartSortDecision } from '@services/AIService';
import { UniqueBridge, type ScannedFile } from '@services/uniqueBridge';

/**
 * Fichier affiché dans l'UI, avec son dossier virtuel courant (relatif à la
 * racine sélectionnée). Au premier scan tous les fichiers sont dans "(racine)".
 * Après rangement, chaque fichier a son nouveau dossier relatif.
 */
interface UIFile extends ScannedFile {
  folder: string;
}

const ROOT_LABEL = '(racine)';

export default function SmartSaveScreen(): JSX.Element {
  const isElectron = UniqueBridge.isElectron();
  const [rootPath, setRootPath] = useState<string | null>(null);
  const [files, setFiles] = useState<UIFile[]>([]);
  const [selected, setSelected] = useState<UIFile | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  const [criteria, setCriteria] = useState<string>(
    'Regrouper par nature de document (budget, stratégie, contrats, comptes-rendus, factures)',
  );
  const [decisions, setDecisions] = useState<SmartSortDecision[]>([]);
  const [sorting, setSorting] = useState(false);
  const [sortError, setSortError] = useState<string | null>(null);

  const [applyReport, setApplyReport] = useState<{
    moved: number;
    failed: number;
    errors: string[];
  } | null>(null);
  const [applying, setApplying] = useState(false);

  const folders = useMemo(() => {
    const set = new Set(files.map((f) => f.folder));
    return Array.from(set).sort();
  }, [files]);

  const tree = useMemo(() => {
    const byFolder: Record<string, UIFile[]> = {};
    for (const f of files) {
      (byFolder[f.folder] = byFolder[f.folder] ?? []).push(f);
    }
    return byFolder;
  }, [files]);

  /* ----- 1. Choisir un dossier ---------------------------------------- */
  const handlePickFolder = async () => {
    setScanError(null);
    setScanning(true);
    try {
      const chosen = await UniqueBridge.pickFolder();
      if (!chosen) {
        setScanning(false);
        return;
      }
      setRootPath(chosen);
      const scanned = await UniqueBridge.scanFolder(chosen);
      const withFolder: UIFile[] = scanned.map((f) => ({ ...f, folder: ROOT_LABEL }));
      setFiles(withFolder);
      setSelected(withFolder[0] ?? null);
      setDecisions([]);
      setApplyReport(null);
      if (withFolder.length === 0) {
        setScanError('Dossier vide — aucun fichier à ranger à la racine.');
      }
    } catch (err) {
      setScanError(err instanceof Error ? err.message : String(err));
    } finally {
      setScanning(false);
    }
  };

  /* ----- 2. Ranger par IA --------------------------------------------- */
  const handleSmartSort = async () => {
    setSorting(true);
    setSortError(null);
    setDecisions([]);
    setApplyReport(null);
    try {
      const res = await AIService.smartSort({
        criteria,
        files: files.map((f) => ({
          id: f.id,
          filename: f.name,
          preview: f.preview || `fichier ${f.extension} (${Math.round(f.size / 1024)} Ko)`,
          current_folder: f.folder === ROOT_LABEL ? null : f.folder,
        })),
        known_folders: folders.filter((f) => f !== ROOT_LABEL),
      });
      setDecisions(res.decisions);
    } catch (err) {
      setSortError(
        err instanceof Error ? err.message : 'Impossible de contacter le backend IA.',
      );
    } finally {
      setSorting(false);
    }
  };

  /* ----- 3. Appliquer les décisions sur le disque --------------------- */
  const handleApply = async () => {
    if (!rootPath || decisions.length === 0) return;
    setApplying(true);
    setApplyReport(null);
    const byId = new Map(decisions.map((d) => [d.file_id, d.target_folder]));

    let moved = 0;
    let failed = 0;
    const errors: string[] = [];
    const newFiles: UIFile[] = [];

    for (const f of files) {
      const targetFolder = byId.get(f.id);
      if (!targetFolder) {
        newFiles.push(f);
        continue;
      }
      const res = await UniqueBridge.moveFile(rootPath, f.absolutePath, targetFolder);
      if (res.ok && res.newPath) {
        moved++;
        newFiles.push({
          ...f,
          folder: targetFolder,
          absolutePath: res.newPath,
        });
      } else {
        failed++;
        errors.push(`${f.name} : ${res.error ?? 'erreur inconnue'}`);
        newFiles.push(f);
      }
    }

    setFiles(newFiles);
    setDecisions([]);
    setApplyReport({ moved, failed, errors });
    setApplying(false);
  };

  const handleReset = () => {
    setRootPath(null);
    setFiles([]);
    setSelected(null);
    setDecisions([]);
    setScanError(null);
    setSortError(null);
    setApplyReport(null);
  };

  /* ----- Rendu --------------------------------------------------------- */
  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <h1>SmartSave Explorer</h1>
      <p className="mt-2 mb-6 text-sm text-[color:var(--color-text-medium)]">
        Sélectionnez un dossier ou un disque, définissez vos critères, et laissez
        l'IA classer automatiquement vos fichiers.
      </p>

      {/* ===== Étape 1 — Sélection du dossier ===== */}
      <UniqueCard variant="glow" padding="lg" className="mb-5">
        <div className="flex items-start gap-3 mb-3">
          <span
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm"
            style={{
              background: 'var(--color-primary)',
              color: 'var(--color-text-dark)',
            }}
          >
            1
          </span>
          <div className="flex-1">
            <h3 className="text-base font-bold m-0">Choisir la source</h3>
            <p className="text-xs text-[color:var(--color-text-medium)] mt-0.5">
              Sélectionnez le dossier ou le disque dont vous voulez ranger les fichiers.
            </p>
          </div>
        </div>

        {!isElectron && (
          <div
            className="mb-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
            style={{
              background: 'rgba(239,83,80,0.08)',
              color: 'var(--color-urgent)',
              border: '1px solid rgba(239,83,80,0.3)',
            }}
          >
            <AlertTriangle size={14} />
            La sélection de dossier nécessite l'application desktop UNIQUE (Electron).
          </div>
        )}

        <div className="flex flex-wrap gap-2 items-center">
          <UniqueButton
            leftIcon={<FolderSearch size={16} />}
            onClick={handlePickFolder}
            isLoading={scanning}
            disabled={!isElectron}
          >
            Choisir un dossier
          </UniqueButton>
          {rootPath && (
            <UniqueButton
              variant="ghost"
              size="sm"
              leftIcon={<RotateCcw size={14} />}
              onClick={handleReset}
            >
              Réinitialiser
            </UniqueButton>
          )}
        </div>

        {rootPath && (
          <div
            className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-mono"
            style={{
              background: 'var(--color-white)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-dark)',
            }}
          >
            <FolderOpen size={14} color="var(--color-primary-dark)" />
            <span className="truncate" title={rootPath}>
              {rootPath}
            </span>
            <span className="ml-auto text-[10px] text-[color:var(--color-text-medium)] font-sans">
              {files.length} fichier{files.length > 1 ? 's' : ''}
            </span>
          </div>
        )}

        {scanError && (
          <p
            className="mt-3 text-xs font-medium px-3 py-2 rounded-lg"
            style={{
              background: 'rgba(239,83,80,0.08)',
              color: 'var(--color-urgent)',
              border: '1px solid rgba(239,83,80,0.3)',
            }}
          >
            ⚠ {scanError}
          </p>
        )}
      </UniqueCard>

      {/* ===== Étape 2 — Critères + classement IA ===== */}
      <UniqueCard
        variant="glow"
        padding="lg"
        className="mb-6"
        style={{ opacity: files.length === 0 ? 0.55 : 1 }}
      >
        <div className="flex items-start gap-3 mb-3">
          <span
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm"
            style={{
              background: 'var(--color-primary)',
              color: 'var(--color-text-dark)',
            }}
          >
            2
          </span>
          <div className="flex-1">
            <h3 className="text-base font-bold m-0 inline-flex items-center gap-2">
              <Sparkles size={16} color="var(--color-primary-dark)" />
              Critères de classement
            </h3>
            <p className="text-xs text-[color:var(--color-text-medium)] mt-0.5">
              Décrivez en langage naturel comment vous voulez que les fichiers soient rangés.
            </p>
          </div>
        </div>

        <label
          className="text-xs font-semibold uppercase tracking-wide block mb-1.5"
          htmlFor="criteria"
        >
          Critère de classement
        </label>
        <textarea
          id="criteria"
          value={criteria}
          onChange={(e) => setCriteria(e.target.value)}
          rows={2}
          placeholder="ex : par année, par département, regrouper les factures, séparer interne/externe…"
          className="unique-input resize-none mb-3"
          disabled={files.length === 0}
        />

        <div className="flex gap-2 flex-wrap">
          <UniqueButton
            leftIcon={<Wand2 size={16} />}
            onClick={handleSmartSort}
            isLoading={sorting}
            disabled={!criteria.trim() || files.length === 0}
          >
            Ranger par IA
          </UniqueButton>
          {decisions.length > 0 && (
            <UniqueButton
              variant="secondary"
              leftIcon={<Check size={16} />}
              onClick={handleApply}
              isLoading={applying}
              disabled={!rootPath}
            >
              Appliquer ({decisions.length})
            </UniqueButton>
          )}
        </div>

        {sortError && (
          <p
            className="mt-3 text-xs font-medium px-3 py-2 rounded-lg"
            style={{
              background: 'rgba(239,83,80,0.08)',
              color: 'var(--color-urgent)',
              border: '1px solid rgba(239,83,80,0.3)',
            }}
          >
            ⚠ {sortError}
          </p>
        )}

        {applyReport && (
          <div
            className="mt-3 px-3 py-2 rounded-lg text-xs"
            style={{
              background:
                applyReport.failed === 0
                  ? 'var(--color-primary-light)'
                  : 'rgba(239,83,80,0.08)',
              border: `1px solid ${
                applyReport.failed === 0
                  ? 'var(--color-primary-dark)'
                  : 'rgba(239,83,80,0.3)'
              }`,
              color:
                applyReport.failed === 0
                  ? 'var(--color-text-dark)'
                  : 'var(--color-urgent)',
            }}
          >
            <p className="font-semibold m-0">
              ✦ {applyReport.moved} fichier(s) déplacé(s)
              {applyReport.failed > 0 && ` · ${applyReport.failed} échec(s)`}
            </p>
            {applyReport.errors.length > 0 && (
              <ul className="mt-1 list-disc list-inside">
                {applyReport.errors.slice(0, 5).map((e, i) => (
                  <li key={i} className="truncate">
                    {e}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </UniqueCard>

      {/* ===== Étape 3 — Décisions proposées ===== */}
      {decisions.length > 0 && (
        <UniqueCard padding="lg" className="mb-6">
          <h3 className="text-sm font-bold mb-3 inline-flex items-center gap-1.5">
            <Wand2 size={14} color="var(--color-primary-dark)" />
            Décisions proposées par l'IA — vérifiez avant d'appliquer
          </h3>
          <ul className="flex flex-col gap-2 max-h-[340px] overflow-y-auto">
            {decisions.map((d) => {
              const file = files.find((f) => f.id === d.file_id);
              return (
                <li
                  key={d.file_id}
                  className="flex items-start gap-3 p-3 rounded-card"
                  style={{
                    background: 'var(--color-primary-glow)',
                    border: '1px solid var(--color-border)',
                  }}
                >
                  <FileText
                    size={16}
                    color="var(--color-primary-dark)"
                    className="mt-0.5 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold m-0 truncate" title={file?.name}>
                      {file?.name ?? d.file_id}
                    </p>
                    <p className="text-[11px] text-[color:var(--color-text-medium)] m-0">
                      {file?.folder === ROOT_LABEL ? 'racine' : file?.folder ?? '—'} →{' '}
                      <strong>{d.target_folder}</strong>
                    </p>
                    <p className="text-[11px] italic text-[color:var(--color-text-medium)] mt-1 m-0">
                      {d.reason}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </UniqueCard>
      )}

      {/* ===== Étape 4 — Arborescence + prévisu ===== */}
      {files.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <UniqueCard padding="md" className="lg:col-span-1">
            <h3 className="text-sm font-bold mb-3">Contenu</h3>
            <ul className="text-sm">
              {Object.entries(tree).map(([folder, fs]) => (
                <li key={folder} className="mb-2">
                  <div className="flex items-center gap-2 py-1 px-2 font-semibold">
                    <FolderOpen size={14} color="var(--color-primary-dark)" />
                    <span className="truncate" title={folder}>
                      {folder}
                    </span>
                    <span
                      className="text-[10px] ml-auto px-1.5 rounded-full"
                      style={{
                        background: 'var(--color-primary-light)',
                        color: 'var(--color-primary-dark)',
                      }}
                    >
                      {fs.length}
                    </span>
                  </div>
                  <ul className="pl-5">
                    {fs.map((file) => (
                      <li key={file.id}>
                        <button
                          type="button"
                          onClick={() => setSelected(file)}
                          className="flex items-center gap-2 w-full text-left py-1 px-2 rounded-lg hover:bg-[color:var(--color-primary-glow)]"
                          style={{
                            background:
                              selected?.id === file.id
                                ? 'var(--color-primary-light)'
                                : 'transparent',
                          }}
                        >
                          <FileText size={13} />
                          <span className="truncate">{file.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </UniqueCard>

          <UniqueCard padding="lg" className="lg:col-span-2">
            {selected ? (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-bold m-0 truncate" title={selected.name}>
                      {selected.name}
                    </h3>
                    <p className="text-xs text-[color:var(--color-text-medium)]">
                      {Math.round(selected.size / 1024)} Ko · modifié{' '}
                      {new Date(selected.modifiedAt).toLocaleString('fr-FR')}
                    </p>
                    <p className="text-[11px] font-mono text-[color:var(--color-text-medium)] truncate">
                      {selected.absolutePath}
                    </p>
                  </div>
                  <UniqueBadge tone="gold">{selected.extension || 'fichier'}</UniqueBadge>
                </div>

                <div
                  className="rounded-card p-5 mb-5 text-sm whitespace-pre-wrap"
                  style={{
                    background: 'var(--color-primary-glow)',
                    border: '1px dashed var(--color-border)',
                    minHeight: 140,
                  }}
                >
                  {selected.preview ||
                    'Aperçu non disponible pour ce type de fichier. Cliquez sur Ouvrir pour le visualiser dans son application.'}
                </div>

                <div className="flex gap-2 flex-wrap">
                  <UniqueButton
                    leftIcon={<Eye size={16} />}
                    onClick={() => void UniqueBridge.openApp(selected.absolutePath)}
                  >
                    Ouvrir
                  </UniqueButton>
                  <UniqueButton variant="secondary" leftIcon={<Share2 size={16} />}>
                    Partager
                  </UniqueButton>
                  <UniqueButton variant="ghost" leftIcon={<Archive size={16} />}>
                    Archiver
                  </UniqueButton>
                </div>
              </>
            ) : (
              <p className="text-sm text-[color:var(--color-text-medium)]">
                Sélectionnez un fichier dans l'arborescence.
              </p>
            )}
          </UniqueCard>
        </div>
      )}
    </div>
  );
}
